import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, Search, Plus, Edit2, Trash2, Save, X, 
  Building2, User, Phone, Mail, FileText, Calendar,
  DollarSign, Hash, MessageSquare, Clock, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Loader2, BookOpen, MapPin, Key, Shield, UserCog, AlertTriangle, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const RecurringClientsManager = ({ classTypes = [] }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [showContactsForClient, setShowContactsForClient] = useState(null); // Which client to show contacts for (vs notes)
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts', 'notes', 'prices', 'locations'
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [availableClassTypes, setAvailableClassTypes] = useState([]);
  
  // Dedicated dialog states for Pricing and Locations
  const [showPricingDialog, setShowPricingDialog] = useState(null); // Client object when showing pricing dialog
  const [showLocationsDialog, setShowLocationsDialog] = useState(null); // Client object when showing locations dialog
  const [editingPricing, setEditingPricing] = useState(null); // Pricing data being edited in dialog
  const [editingLocations, setEditingLocations] = useState(null); // Locations data being edited in dialog
  const [savingPricing, setSavingPricing] = useState(false);
  const [savingLocations, setSavingLocations] = useState(false);
  
  // Contact management state
  const [showAddContact, setShowAddContact] = useState(null); // Client ID when adding contact
  const [editingContact, setEditingContact] = useState(null); // Contact ID when editing
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
  const [savingContact, setSavingContact] = useState(false);
  
  // Password and permissions management
  const [setPasswordClient, setSetPasswordClient] = useState(null); // {clientId, isPrimary, contactIndex}
  const [tempPassword, setTempPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [swapPrimaryClient, setSwapPrimaryClient] = useState(null); // Client to swap primary for
  const [swapTargetIndex, setSwapTargetIndex] = useState(null);
  const [swappingPrimary, setSwappingPrimary] = useState(false);
  
  // Contact permissions management
  const [editPermissionsContact, setEditPermissionsContact] = useState(null); // {clientId, contactIndex, contact}
  const [permissionsForm, setPermissionsForm] = useState({
    classes: true,
    book_class: true,
    class_pricing: true,
    invoices: false,
    saved_locations: true,
    team_contacts: false,
    make_payments: false
  });
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Password validation
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    company: '',
    preferred_classes: [],  // New format: [{class_id, custom_price}]
    locations: [],  // Format: [{id, name, address: {street, city, state, zip}}]
    restrict_bookings: false  // If true, client cannot book through portal
  });
  
  // WooCommerce prices for class types
  const [classPrices, setClassPrices] = useState({});

  // Fetch class types and their prices
  useEffect(() => {
    const fetchClassTypesAndPrices = async () => {
      let types = classTypes;
      if (classTypes.length === 0) {
        try {
          const response = await axios.get(`${API}/api/class-types`);
          types = response.data || [];
        } catch (error) {
          console.error('Error fetching class types:', error);
          return;
        }
      }
      setAvailableClassTypes(types);
      
      // Fetch prices for each class type
      const prices = {};
      for (const ct of types) {
        try {
          // Get price from class-types endpoint which includes pricing
          const priceResponse = await axios.get(`${API}/api/class-types`);
          const classWithPrice = priceResponse.data?.find(c => c.id === ct.id);
          if (classWithPrice?.prices?.price) {
            prices[ct.id] = parseFloat(classWithPrice.prices.price);
          }
        } catch (e) {
          // Ignore price fetch errors
        }
      }
      setClassPrices(prices);
    };
    fetchClassTypesAndPrices();
  }, [classTypes]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/recurring-clients`, {
        params: { search: searchQuery }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load recurring clients');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(fetchClients, 300);
    return () => clearTimeout(debounce);
  }, [fetchClients]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      title: '',
      email: '',
      phone: '',
      company: '',
      preferred_classes: [],
      locations: [],
      restrict_bookings: false
    });
    setShowAddForm(false);
    setEditingClient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('First and last name are required');
      return;
    }

    try {
      // Also set preferred_class_ids for backwards compatibility
      const submitData = {
        ...formData,
        preferred_class_ids: formData.preferred_classes.map(pc => pc.class_id)
      };
      
      if (editingClient) {
        await axios.put(`${API}/api/admin/recurring-clients/${editingClient.id}`, submitData);
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API}/api/admin/recurring-clients`, submitData);
        toast.success('Client added successfully');
      }
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  // Save pricing from the pricing dialog
  const handleSavePricing = async (clientId, preferredClasses) => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/api/admin/recurring-clients/${clientId}`, {
        preferred_classes: preferredClasses,
        preferred_class_ids: preferredClasses.map(pc => pc.class_id)
      });
      toast.success('Pricing saved successfully');
      fetchClients();
      setEditingPricing(null);
      // Update the dialog's client data
      const updatedClient = { ...showPricingDialog, preferred_classes: preferredClasses };
      setShowPricingDialog(updatedClient);
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  // Save locations from the locations dialog
  const handleSaveLocations = async (clientId, locations) => {
    setSavingLocations(true);
    try {
      await axios.put(`${API}/api/admin/recurring-clients/${clientId}`, {
        locations: locations
      });
      toast.success('Locations saved successfully');
      fetchClients();
      setEditingLocations(null);
      // Update the dialog's client data
      const updatedClient = { ...showLocationsDialog, locations: locations };
      setShowLocationsDialog(updatedClient);
    } catch (error) {
      console.error('Error saving locations:', error);
      toast.error(error.response?.data?.detail || 'Failed to save locations');
    } finally {
      setSavingLocations(false);
    }
  };

  const handleEdit = (client) => {
    // Convert old format to new if needed
    let preferredClasses = client.preferred_classes || [];
    if (preferredClasses.length === 0 && client.preferred_class_ids?.length > 0) {
      // Convert legacy format
      preferredClasses = client.preferred_class_ids.map(classId => ({
        class_id: classId,
        custom_price: null
      }));
    }
    
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      title: client.title || '',
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      preferred_classes: preferredClasses,
      locations: client.locations || [],
      restrict_bookings: client.restrict_bookings || false
    });
    setEditingClient(client);
    setShowAddForm(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/recurring-clients/${clientId}`);
      toast.success('Client deleted');
      fetchClients();
      if (expandedClient === clientId) {
        setExpandedClient(null);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
  };

  const handleAddNote = async (clientId) => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      setSavingNote(true);
      await axios.post(`${API}/api/admin/recurring-clients/${clientId}/notes`, {
        text: newNote.trim()
      });
      toast.success('Note added');
      setNewNote('');
      fetchClients();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (clientId, noteId) => {
    try {
      await axios.delete(`${API}/api/admin/recurring-clients/${clientId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchClients();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Contact management functions
  const handleAddContact = async (clientId) => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    // Validate password if provided
    if (contactForm.temporary_password) {
      const errors = validatePassword(contactForm.temporary_password);
      if (errors.length > 0) {
        toast.error(`Password requirements: ${errors.join(', ')}`);
        return;
      }
    }
    
    setSavingContact(true);
    try {
      const response = await axios.post(`${API}/api/admin/recurring-clients/${clientId}/contacts`, contactForm);
      
      if (response.data.success) {
        // Update local state
        setClients(clients.map(c => 
          c.id === clientId 
            ? { ...c, contacts: [...(c.contacts || []), response.data.contact] }
            : c
        ));
        setShowAddContact(null);
        setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
        
        // Show appropriate success message
        if (response.data.email_sent) {
          toast.success('Team member added! Login instructions sent to their email.');
        } else if (contactForm.temporary_password) {
          toast.success('Team member added with password set.');
        } else {
          toast.success('Team member added. Set their password so they can log in.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleUpdateContact = async (clientId, contactId) => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    setSavingContact(true);
    try {
      const response = await axios.put(
        `${API}/api/admin/recurring-clients/${clientId}/contacts/${contactId}`,
        contactForm
      );
      
      if (response.data.success) {
        setClients(clients.map(c => 
          c.id === clientId 
            ? { ...c, contacts: (c.contacts || []).map(ct => ct.id === contactId ? response.data.contact : ct) }
            : c
        ));
        setEditingContact(null);
        setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '' });
        toast.success('Contact updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (clientId, contactId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/admin/recurring-clients/${clientId}/contacts/${contactId}`);
      
      setClients(clients.map(c => 
        c.id === clientId 
          ? { ...c, contacts: (c.contacts || []).filter(ct => ct.id !== contactId) }
          : c
      ));
      toast.success('Team member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove contact');
    }
  };

  // Set/reset password for primary or team member
  const handleSetPassword = async () => {
    if (!tempPassword) {
      toast.error('Please enter a password');
      return;
    }
    
    const errors = validatePassword(tempPassword);
    if (errors.length > 0) {
      toast.error(`Password requirements: ${errors.join(', ')}`);
      return;
    }
    
    setSettingPassword(true);
    try {
      const { clientId, isPrimary, contactIndex } = setPasswordClient;
      const response = await axios.put(
        `${API}/api/admin/recurring-clients/${clientId}/set-password?is_primary=${isPrimary}&contact_index=${contactIndex}`,
        { password: tempPassword }
      );
      
      if (response.data.email_sent) {
        toast.success('Password set! Login instructions sent to their email.');
      } else {
        toast.success('Password set successfully. User will be prompted to change on first login.');
      }
      setSetPasswordClient(null);
      setTempPassword('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  // Swap primary contact with a team member
  const handleSwapPrimary = async () => {
    if (swapTargetIndex === null) {
      toast.error('Please select a team member');
      return;
    }
    
    setSwappingPrimary(true);
    try {
      await axios.post(
        `${API}/api/admin/recurring-clients/${swapPrimaryClient.id}/swap-primary`,
        { contact_index: swapTargetIndex }
      );
      
      toast.success('Primary contact changed successfully');
      setSwapPrimaryClient(null);
      setSwapTargetIndex(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to swap primary');
    } finally {
      setSwappingPrimary(false);
    }
  };

  // Update contact permissions
  const handleUpdatePermissions = async () => {
    if (!editPermissionsContact) return;
    
    setSavingPermissions(true);
    try {
      await axios.put(
        `${API}/api/admin/recurring-clients/${editPermissionsContact.clientId}/contacts/${editPermissionsContact.contact.id}/permissions`,
        permissionsForm
      );
      
      toast.success('Permissions updated successfully');
      setEditPermissionsContact(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Recurring Clients
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage your frequent customers and their booking history
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="add-recurring-client-btn"
        >
          <Plus className="w-4 h-4" />
          Add Client
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="search"
          name="recurring_client_search"
          autoComplete="off"
          placeholder="Search by name, email, phone, company, or team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          data-testid="recurring-clients-search"
        />
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            {editingClient ? (
              <>
                <Edit2 className="w-5 h-5 text-blue-600" />
                Edit Client
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 text-green-600" />
                Add New Client
              </>
            )}
          </h3>
          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="client_first_name"
                  autoComplete="new-password"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="John"
                  required
                  data-testid="client-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="client_last_name"
                  autoComplete="new-password"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Smith"
                  required
                  data-testid="client-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title / Position
                </label>
                <input
                  type="text"
                  name="client_title"
                  autoComplete="new-password"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Safety Director"
                  data-testid="client-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company / Business
                </label>
                <input
                  type="text"
                  name="client_company"
                  autoComplete="new-password"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="ACME Corporation"
                  data-testid="client-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="client_email"
                  autoComplete="new-password"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="john@company.com"
                  data-testid="client-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="client_phone"
                  autoComplete="new-password"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="(555) 123-4567"
                  data-testid="client-phone"
                />
              </div>
            </div>

            {/* Restrict Bookings Toggle */}
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Restrict Bookings</p>
                  <p className="text-xs text-gray-500">Prevent this client from booking classes through the portal</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, restrict_bookings: !formData.restrict_bookings })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.restrict_bookings ? 'bg-red-500' : 'bg-gray-300'
                }`}
                data-testid="restrict-bookings-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.restrict_bookings ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="save-client-btn"
              >
                <Save className="w-4 h-4" />
                {editingClient ? 'Update Client' : 'Save Client'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {searchQuery ? 'No clients found matching your search' : 'No recurring clients yet'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Add your first recurring client
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map((client) => (
            <div
              key={client.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Client Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {client.company ? (
                        <Building2 className="w-5 h-5 text-blue-600" />
                      ) : (
                        <User className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {client.company ? (
                          <>
                            <span className="text-blue-600">{client.company}</span>
                            <span className="text-gray-400 mx-2">-</span>
                          </>
                        ) : null}
                        {client.first_name} {client.last_name}
                        {client.restrict_bookings && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Restricted
                          </span>
                        )}
                        {/* Payment Method Indicator */}
                        {client.vault_payment_token ? (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full inline-flex items-center gap-1" title={`${client.vault_card_brand || 'Card'} ending in ${client.vault_card_last_four || '****'}`}>
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            Auto-Pay •••{client.vault_card_last_four || '****'}
                          </span>
                        ) : (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full inline-flex items-center gap-1" title="Client will receive invoices for payment">
                            <FileText className="w-3 h-3" />
                            Invoice
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {client.email}
                          </span>
                        )}
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {client.phone}
                          </span>
                        )}
                      </div>
                      {/* Preferred Classes Badges with Custom Pricing */}
                      {client.preferred_classes && client.preferred_classes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.preferred_classes.map((pc) => {
                            const classType = availableClassTypes.find(c => c.id === pc.class_id);
                            const hasCustomPrice = pc.custom_price !== null && pc.custom_price !== undefined;
                            const hasExpiry = pc.price_expires_at;
                            const isExpired = hasExpiry && new Date(pc.price_expires_at) <= new Date();
                            const expiresIn = hasExpiry ? Math.ceil((new Date(pc.price_expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                            
                            return classType ? (
                              <span 
                                key={pc.class_id} 
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                                  hasCustomPrice && !isExpired
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                                title={hasCustomPrice && !isExpired 
                                  ? `Custom: $${pc.custom_price}${hasExpiry ? ` (expires ${expiresIn > 0 ? `in ${expiresIn}d` : 'today'})` : ''}`
                                  : 'Default pricing'}
                              >
                                <BookOpen className="w-3 h-3" />
                                {classType.name}
                                {hasCustomPrice && !isExpired && (
                                  <>
                                    <span className="text-green-600 font-semibold">${pc.custom_price}</span>
                                    {hasExpiry && expiresIn !== null && expiresIn <= 7 && (
                                      <Clock className="w-3 h-3 text-amber-500" />
                                    )}
                                  </>
                                )}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      {/* Fallback for old format (preferred_class_ids without pricing) */}
                      {(!client.preferred_classes || client.preferred_classes.length === 0) && client.preferred_class_ids && client.preferred_class_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.preferred_class_ids.map((classId) => {
                            const classType = availableClassTypes.find(c => c.id === classId);
                            return classType ? (
                              <span 
                                key={classId} 
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                              >
                                <BookOpen className="w-3 h-3" />
                                {classType.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Stats Badges */}
                    <div className="hidden sm:flex items-center gap-3 mr-4">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">{client.total_bookings || 0}</div>
                        <div className="text-xs text-gray-500">Classes</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-blue-600">{client.total_students || 0}</div>
                        <div className="text-xs text-gray-500">Students</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-green-600">{formatCurrency(client.total_revenue)}</div>
                        <div className="text-xs text-gray-500">Revenue</div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(client);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit Client Info"
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedClient(client.id);
                        setActiveTab('contacts');
                      }}
                      className={`p-2 rounded-lg transition-colors relative ${
                        expandedClient === client.id && activeTab === 'contacts'
                          ? 'text-purple-600 bg-purple-50'
                          : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                      title="Team Contacts"
                      data-testid={`team-contacts-${client.id}`}
                    >
                      <Users className="w-4 h-4" />
                      {(client.contacts || []).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {client.contacts.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPricingDialog(client);
                      }}
                      className={`p-2 rounded-lg transition-colors relative ${
                        showPricingDialog?.id === client.id
                          ? 'text-green-600 bg-green-50'
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title="Preferred Classes & Pricing"
                      data-testid={`prices-${client.id}`}
                    >
                      <DollarSign className="w-4 h-4" />
                      {(client.preferred_classes || []).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {client.preferred_classes.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLocationsDialog(client);
                      }}
                      className={`p-2 rounded-lg transition-colors relative ${
                        showLocationsDialog?.id === client.id
                          ? 'text-orange-600 bg-orange-50'
                          : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                      }`}
                      title="Saved Locations"
                      data-testid={`locations-${client.id}`}
                    >
                      <MapPin className="w-4 h-4" />
                      {(client.locations || []).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {client.locations.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedClient(client.id);
                        setActiveTab('notes');
                      }}
                      className={`p-2 rounded-lg transition-colors relative ${
                        expandedClient === client.id && activeTab === 'notes'
                          ? 'text-amber-600 bg-amber-50'
                          : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                      }`}
                      title="Notes"
                      data-testid={`notes-${client.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      {(client.notes || []).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {client.notes.length > 9 ? '9+' : client.notes.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(client.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Client"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Mobile Stats */}
                <div className="flex sm:hidden items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-sm">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{client.total_bookings || 0}</span>
                    <span className="text-gray-500">classes</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-blue-600">{client.total_students || 0}</span>
                    <span className="text-gray-500">students</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="font-medium text-green-600">{formatCurrency(client.total_revenue)}</span>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedClient === client.id && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  {/* Tab Buttons - Contacts and Notes only (Prices & Locations moved to dialogs) */}
                  <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                    <button
                      onClick={() => setActiveTab('contacts')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                        activeTab === 'contacts'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      Team Contacts
                      {(client.contacts || []).length > 0 && (
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          activeTab === 'contacts' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {client.contacts.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('notes')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                        activeTab === 'notes'
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      Notes
                      {(client.notes || []).length > 0 && (
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          activeTab === 'notes' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {client.notes.length}
                        </span>
                      )}
                    </button>
                  </div>
                  
                  {/* Stats Row */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <Hash className="w-4 h-4" />
                        Total Classes
                      </div>
                      <div className="text-xl font-semibold text-gray-900">{client.total_bookings || 0}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        Total Students
                      </div>
                      <div className="text-xl font-semibold text-blue-600">{client.total_students || 0}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <DollarSign className="w-4 h-4" />
                        Total Revenue
                      </div>
                      <div className="text-xl font-semibold text-green-600">{formatCurrency(client.total_revenue)}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <Calendar className="w-4 h-4" />
                        Last Booking
                      </div>
                      <div className="text-lg font-semibold text-gray-900">{formatDate(client.last_booking_date)}</div>
                    </div>
                  </div>

                  {/* Team Contacts Section */}
                  {activeTab === 'contacts' && (
                  <div className="bg-white p-4 rounded-lg border border-gray-100 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Team Contacts
                        <span className="text-sm font-normal text-gray-500">({(client.contacts || []).length} additional)</span>
                      </h4>
                      <button
                        onClick={() => {
                          setShowAddContact(client.id);
                          setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '' });
                        }}
                        className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Add Contact
                      </button>
                    </div>

                    {/* Primary Contact */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                              <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded">Primary</span>
                              {client.password_hash ? (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-0.5 inline-flex">
                                  <Key className="w-3 h-3" /> Has Password
                                </span>
                              ) : (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded flex items-center gap-0.5 inline-flex">
                                  <Key className="w-3 h-3" /> No Password
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">{client.email} • {client.phone || 'No phone'}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSetPasswordClient({ clientId: client.id, isPrimary: true, contactIndex: 0 })}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center gap-1"
                          data-testid={`set-password-primary-${client.id}`}
                        >
                          <Key className="w-3 h-3" />
                          {client.password_hash ? 'Reset Password' : 'Set Password'}
                        </button>
                      </div>
                    </div>

                    {/* Add Contact Form */}
                    {showAddContact === client.id && (
                      <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleAddContact(client.id); }} className="p-3 bg-gray-50 rounded-lg border-2 border-dashed border-blue-300 mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">New Team Member</p>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <input
                            type="text"
                            name="contact_first_name"
                            autoComplete="new-password"
                            placeholder="First Name *"
                            value={contactForm.first_name}
                            onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                            className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            data-testid="contact-first-name"
                          />
                          <input
                            type="text"
                            name="contact_last_name"
                            autoComplete="new-password"
                            placeholder="Last Name *"
                            value={contactForm.last_name}
                            onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                            className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            data-testid="contact-last-name"
                          />
                        </div>
                        <input
                          type="email"
                          name="contact_email"
                          autoComplete="new-password"
                          placeholder="Email * (they will use this to log in)"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 mb-2"
                          data-testid="contact-email"
                        />
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <input
                            type="tel"
                            name="contact_phone"
                            autoComplete="new-password"
                            placeholder="Phone"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                            className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            data-testid="contact-phone"
                          />
                          <input
                            type="text"
                            name="contact_title"
                            autoComplete="new-password"
                            placeholder="Title/Role"
                            value={contactForm.title}
                            onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                            className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            data-testid="contact-title"
                          />
                        </div>
                        {/* Initial Password Field */}
                        <div className="mb-2">
                          <input
                            type="password"
                            name="contact_password"
                            autoComplete="new-password"
                            placeholder="Initial Password (optional - or set later)"
                            value={contactForm.temporary_password}
                            onChange={(e) => setContactForm({ ...contactForm, temporary_password: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            data-testid="contact-password"
                          />
                          {contactForm.temporary_password && (
                            <div className="mt-1 text-xs space-y-0.5">
                              <p className={contactForm.temporary_password.length >= 8 ? 'text-green-600' : 'text-gray-400'}>
                                {contactForm.temporary_password.length >= 8 ? '✓' : '○'} 8+ characters
                              </p>
                              <p className={/[A-Z]/.test(contactForm.temporary_password) && /[a-z]/.test(contactForm.temporary_password) ? 'text-green-600' : 'text-gray-400'}>
                                {/[A-Z]/.test(contactForm.temporary_password) && /[a-z]/.test(contactForm.temporary_password) ? '✓' : '○'} Upper & lowercase
                              </p>
                              <p className={/[0-9]/.test(contactForm.temporary_password) && /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(contactForm.temporary_password) ? 'text-green-600' : 'text-gray-400'}>
                                {/[0-9]/.test(contactForm.temporary_password) && /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(contactForm.temporary_password) ? '✓' : '○'} Number & special char
                              </p>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            They&apos;ll receive an email with login instructions
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={savingContact}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddContact(null);
                              setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
                            }}
                            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Secondary Contacts List */}
                    {(client.contacts || []).length > 0 ? (
                      <div className="space-y-2">
                        {(client.contacts || []).map((contact) => (
                          <div key={contact.id} className="p-3 bg-gray-50 rounded-lg">
                            {editingContact === contact.id ? (
                              // Edit mode
                              <div>
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <input
                                    type="text"
                                    placeholder="First Name *"
                                    value={contactForm.first_name}
                                    onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Last Name *"
                                    value={contactForm.last_name}
                                    onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                                <input
                                  type="email"
                                  placeholder="Email *"
                                  value={contactForm.email}
                                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 mb-2"
                                />
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <input
                                    type="text"
                                    placeholder="Phone"
                                    value={contactForm.phone}
                                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Title/Role"
                                    value={contactForm.title}
                                    onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleUpdateContact(client.id, contact.id)}
                                    disabled={savingContact}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingContact(null);
                                      setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '' });
                                    }}
                                    className="px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded hover:bg-gray-100"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // View mode
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-gray-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {contact.first_name} {contact.last_name}
                                      {contact.password_hash ? (
                                        <span className="ml-2 px-1 py-0.5 text-xs bg-green-100 text-green-700 rounded inline-flex items-center gap-0.5">
                                          <Key className="w-2.5 h-2.5" /> PW
                                        </span>
                                      ) : (
                                        <span className="ml-2 px-1 py-0.5 text-xs bg-amber-100 text-amber-700 rounded inline-flex items-center gap-0.5">
                                          <Key className="w-2.5 h-2.5" /> No PW
                                        </span>
                                      )}
                                    </p>
                                    {contact.title && <p className="text-xs text-gray-500">{contact.title}</p>}
                                    <p className="text-xs text-gray-500">{contact.email} {contact.phone && `• ${contact.phone}`}</p>
                                  </div>
                                </div>
                                <div className="flex gap-1 flex-wrap justify-end">
                                  {/* Make Primary Button */}
                                  <button
                                    onClick={() => {
                                      setSwapPrimaryClient(client);
                                      setSwapTargetIndex((client.contacts || []).findIndex(c => c.id === contact.id));
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-purple-600 rounded"
                                    title="Make Primary Contact"
                                    data-testid={`swap-primary-${contact.id}`}
                                  >
                                    <UserCog className="w-4 h-4" />
                                  </button>
                                  {/* Set Password Button */}
                                  <button
                                    onClick={() => setSetPasswordClient({ 
                                      clientId: client.id, 
                                      isPrimary: false, 
                                      contactIndex: (client.contacts || []).findIndex(c => c.id === contact.id),
                                      contactName: `${contact.first_name} ${contact.last_name}`
                                    })}
                                    className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                                    title={contact.password_hash ? 'Reset Password' : 'Set Password'}
                                    data-testid={`set-password-${contact.id}`}
                                  >
                                    <Key className="w-4 h-4" />
                                  </button>
                                  {/* Permissions Button */}
                                  <button
                                    onClick={() => {
                                      const contactPerms = contact.permissions || {};
                                      setEditPermissionsContact({
                                        clientId: client.id,
                                        contactIndex: (client.contacts || []).findIndex(c => c.id === contact.id),
                                        contact: contact
                                      });
                                      setPermissionsForm({
                                        classes: contactPerms.classes !== false,
                                        book_class: contactPerms.book_class !== false,
                                        class_pricing: contactPerms.class_pricing !== false,
                                        invoices: contactPerms.invoices === true,
                                        saved_locations: contactPerms.saved_locations !== false,
                                        team_contacts: contactPerms.team_contacts === true,
                                        make_payments: contactPerms.make_payments === true
                                      });
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-amber-600 rounded"
                                    title="Edit Permissions"
                                    data-testid={`edit-permissions-${contact.id}`}
                                  >
                                    <Shield className="w-4 h-4" />
                                  </button>
                                  {/* Edit Button */}
                                  <button
                                    onClick={() => {
                                      setEditingContact(contact.id);
                                      setContactForm({
                                        first_name: contact.first_name || '',
                                        last_name: contact.last_name || '',
                                        email: contact.email || '',
                                        phone: contact.phone || '',
                                        title: contact.title || ''
                                      });
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                                    title="Edit Contact"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  {/* Delete Button */}
                                  <button
                                    onClick={() => handleDeleteContact(client.id, contact.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                                    title="Delete Contact"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-2">No additional team contacts</p>
                    )}
                  </div>
                  )}

                  {/* Notes Section - Only show when notes tab is active */}
                  {activeTab === 'notes' && (
                  <div className="bg-white p-4 rounded-lg border border-gray-100 mt-4">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-amber-600" />
                      Notes
                    </h4>

                    {/* Add Note */}
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note..."
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddNote(client.id);
                          }
                        }}
                        data-testid={`add-note-input-${client.id}`}
                      />
                      <button
                        onClick={() => handleAddNote(client.id)}
                        disabled={savingNote || !newNote.trim()}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                        data-testid={`add-note-btn-${client.id}`}
                      >
                        {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                      </button>
                    </div>

                    {/* Notes List */}
                    {client.notes && client.notes.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {[...client.notes].reverse().map((note) => (
                          <div
                            key={note.id}
                            className="flex items-start justify-between gap-2 p-3 bg-gray-50 rounded-lg group"
                          >
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">{note.text}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                                <Clock className="w-3 h-3" />
                                {formatDateTime(note.created_at)}
                                {note.created_by && (
                                  <>
                                    <span>•</span>
                                    <span>{note.created_by}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteNote(client.id, note.id)}
                              className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                              data-testid={`delete-note-${note.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-4">No notes yet</p>
                    )}
                  </div>
                  )}

                  {/* Prices and Locations now open as separate dialogs via buttons above */}

                  {/* Created Info */}
                  <div className="mt-4 text-xs text-gray-400 flex items-center gap-4">
                    <span>Created: {formatDateTime(client.created_at)}</span>
                    {client.updated_at !== client.created_at && (
                      <span>Updated: {formatDateTime(client.updated_at)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Set Password Dialog */}
      <Dialog open={!!setPasswordClient} onOpenChange={() => { setSetPasswordClient(null); setTempPassword(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              {setPasswordClient?.isPrimary ? 'Set Primary Contact Password' : `Set Password for ${setPasswordClient?.contactName || 'Team Member'}`}
            </DialogTitle>
            <DialogDescription>
              Enter a temporary password. The user will be required to change it on first login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="temp-password">Temporary Password</Label>
              <Input
                id="temp-password"
                type="text"
                placeholder="Enter temporary password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                className="mt-1"
                data-testid="temp-password-input"
              />
            </div>
            
            {/* Password requirements */}
            <div className="text-xs space-y-1 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 mb-2">Password Requirements:</p>
              <p className={tempPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}>
                {tempPassword.length >= 8 ? '✓' : '○'} At least 8 characters
              </p>
              <p className={/[A-Z]/.test(tempPassword) ? 'text-green-600' : 'text-gray-500'}>
                {/[A-Z]/.test(tempPassword) ? '✓' : '○'} One uppercase letter
              </p>
              <p className={/[a-z]/.test(tempPassword) ? 'text-green-600' : 'text-gray-500'}>
                {/[a-z]/.test(tempPassword) ? '✓' : '○'} One lowercase letter
              </p>
              <p className={/[0-9]/.test(tempPassword) ? 'text-green-600' : 'text-gray-500'}>
                {/[0-9]/.test(tempPassword) ? '✓' : '○'} One number
              </p>
              <p className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(tempPassword) ? 'text-green-600' : 'text-gray-500'}>
                {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(tempPassword) ? '✓' : '○'} One special character
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSetPasswordClient(null); setTempPassword(''); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={settingPassword || validatePassword(tempPassword).length > 0}
              data-testid="confirm-set-password-btn"
            >
              {settingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap Primary Confirmation Dialog */}
      <Dialog open={!!swapPrimaryClient} onOpenChange={() => { setSwapPrimaryClient(null); setSwapTargetIndex(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-600" />
              Change Primary Contact
            </DialogTitle>
            <DialogDescription>
              This will swap the primary contact role with the selected team member. The current primary will become a team member with default permissions.
            </DialogDescription>
          </DialogHeader>
          
          {swapPrimaryClient && swapTargetIndex !== null && (
            <div className="py-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Current Primary:</strong> {swapPrimaryClient.first_name} {swapPrimaryClient.last_name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>New Primary:</strong> {(swapPrimaryClient.contacts || [])[swapTargetIndex]?.first_name} {(swapPrimaryClient.contacts || [])[swapTargetIndex]?.last_name}
                </p>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-xs text-amber-700">
                  The new primary contact will have full admin access. The current primary&apos;s permissions will be reset to default team member permissions.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSwapPrimaryClient(null); setSwapTargetIndex(null); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSwapPrimary}
              disabled={swappingPrimary}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-swap-primary-btn"
            >
              {swappingPrimary ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Swap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Permissions Dialog */}
      <Dialog open={!!editPermissionsContact} onOpenChange={() => setEditPermissionsContact(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Edit Contact Permissions
            </DialogTitle>
            <DialogDescription>
              {editPermissionsContact?.contact?.first_name} {editPermissionsContact?.contact?.last_name} ({editPermissionsContact?.contact?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              Configure what this team member can do in the client portal. These are the same options the primary contact can set.
            </p>
            
            {/* View Classes Tab */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-gray-900">View Classes</p>
                  <p className="text-xs text-gray-500">Can see the Classes tab and view upcoming/past classes</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.classes}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, classes: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            {/* Book Classes */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900">Book Classes</p>
                  <p className="text-xs text-gray-500">Can register for classes on behalf of the company</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.book_class}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, book_class: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
            
            {/* View Class Pricing */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-gray-900">View Class Pricing</p>
                  <p className="text-xs text-gray-500">Can see class prices and custom pricing tiers</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.class_pricing}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, class_pricing: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
            
            {/* View Invoices */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-gray-900">View Invoices</p>
                  <p className="text-xs text-gray-500">Can view and manage company invoices and billing</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.invoices}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, invoices: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
              </label>
            </div>
            
            {/* Saved Locations */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium text-gray-900">Saved Locations</p>
                  <p className="text-xs text-gray-500">Can view and manage saved training locations</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.saved_locations}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, saved_locations: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            
            {/* Team Contacts */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-cyan-500" />
                <div>
                  <p className="font-medium text-gray-900">View Team Contacts</p>
                  <p className="text-xs text-gray-500">Can see other team members in the portal</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.team_contacts}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, team_contacts: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
              </label>
            </div>
            
            {/* Make Payments */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium text-gray-900">Make Payments</p>
                  <p className="text-xs text-gray-500">Can use saved card to pay for classes and invoices</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissionsForm.make_payments}
                  onChange={(e) => setPermissionsForm(prev => ({ ...prev, make_payments: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditPermissionsContact(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePermissions}
              disabled={savingPermissions}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-permissions-btn"
            >
              {savingPermissions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preferred Classes & Pricing Dialog */}
      <Dialog open={!!showPricingDialog} onOpenChange={() => { setShowPricingDialog(null); setEditingPricing(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Preferred Classes & Pricing
            </DialogTitle>
            <DialogDescription>
              {showPricingDialog?.company ? `${showPricingDialog.company} - ` : ''}{showPricingDialog?.first_name} {showPricingDialog?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Edit Mode */}
            {editingPricing !== null ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 mb-2">Select classes and set custom prices. Leave price blank to use default pricing.</p>
                <div className="border border-gray-200 rounded-lg p-3 max-h-96 overflow-y-auto bg-gray-50">
                  {availableClassTypes.length === 0 ? (
                    <p className="text-sm text-gray-400">No class types available</p>
                  ) : (
                    <div className="space-y-3">
                      {availableClassTypes.map((classType) => {
                        const isSelected = editingPricing.some(pc => pc.class_id === classType.id);
                        const selectedClass = editingPricing.find(pc => pc.class_id === classType.id);
                        const defaultPrice = classType.prices?.price || classPrices[classType.id] || 0;
                        
                        return (
                          <div 
                            key={classType.id} 
                            className={`p-3 rounded-lg border transition-colors ${
                              isSelected ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingPricing([...editingPricing, { class_id: classType.id, custom_price: null }]);
                                  } else {
                                    setEditingPricing(editingPricing.filter(pc => pc.class_id !== classType.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-sm font-medium text-gray-700 flex-1">{classType.name}</span>
                              {defaultPrice > 0 && (
                                <span className="text-xs text-gray-400">Default: ${defaultPrice}</span>
                              )}
                            </label>
                            
                            {isSelected && (
                              <div className="mt-2 ml-6 space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">Custom Price:</span>
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder={defaultPrice > 0 ? `${defaultPrice}` : 'Default'}
                                      value={selectedClass?.custom_price ?? ''}
                                      onChange={(e) => {
                                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                                        setEditingPricing(editingPricing.map(pc => 
                                          pc.class_id === classType.id 
                                            ? { ...pc, custom_price: value, price_expires_at: value === null ? null : pc.price_expires_at }
                                            : pc
                                        ));
                                      }}
                                      className="w-24 pl-5 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                    />
                                  </div>
                                  {selectedClass?.custom_price !== null && selectedClass?.custom_price < defaultPrice && (
                                    <span className="text-xs text-green-600 flex items-center gap-1">
                                      <DollarSign className="w-3 h-3" />
                                      Discounted
                                    </span>
                                  )}
                                </div>
                                
                                {selectedClass?.custom_price !== null && selectedClass?.custom_price !== undefined && (
                                  <div className="flex items-center gap-2 bg-amber-50 p-2 rounded border border-amber-200">
                                    <Clock className="w-3 h-3 text-amber-600" />
                                    <span className="text-xs text-amber-700">Expires:</span>
                                    <input
                                      type="date"
                                      value={selectedClass?.price_expires_at ? selectedClass.price_expires_at.split('T')[0] : ''}
                                      onChange={(e) => {
                                        const dateValue = e.target.value;
                                        const value = dateValue ? new Date(dateValue + 'T23:59:59').toISOString() : null;
                                        setEditingPricing(editingPricing.map(pc => 
                                          pc.class_id === classType.id 
                                            ? { ...pc, price_expires_at: value }
                                            : pc
                                        ));
                                      }}
                                      className="px-2 py-0.5 text-xs border border-amber-300 rounded focus:ring-1 focus:ring-amber-500"
                                    />
                                    {selectedClass?.price_expires_at && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingPricing(editingPricing.map(pc => 
                                            pc.class_id === classType.id 
                                              ? { ...pc, price_expires_at: null }
                                              : pc
                                          ));
                                        }}
                                        className="text-xs text-red-500 hover:text-red-700"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* View Mode */
              showPricingDialog?.preferred_classes && showPricingDialog.preferred_classes.length > 0 ? (
                <div className="space-y-3">
                  {showPricingDialog.preferred_classes.map((pc, idx) => {
                    const classType = availableClassTypes.find(c => c.id === pc.class_id);
                    const hasExpiry = pc.price_expires_at;
                    const isExpired = hasExpiry && new Date(pc.price_expires_at) <= new Date();
                    const expiresIn = hasExpiry ? Math.ceil((new Date(pc.price_expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                    
                    return (
                      <div key={idx} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-green-600" />
                            {classType?.name || pc.class_name || `Class ${pc.class_id}`}
                          </p>
                          {hasExpiry && !isExpired && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Expires {expiresIn > 0 ? `in ${expiresIn} days` : 'today'}
                            </p>
                          )}
                          {isExpired && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Price expired - using default
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {pc.custom_price !== null && pc.custom_price !== undefined && !isExpired ? (
                            <span className="text-xl font-bold text-green-600">${pc.custom_price.toFixed(2)}</span>
                          ) : (
                            <span className="text-sm text-gray-500">Default pricing</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No preferred classes configured</p>
                  <p className="text-xs text-gray-400 mt-1">Click the edit button below to add classes with custom pricing</p>
                </div>
              )
            )}
          </div>
          
          <DialogFooter>
            {editingPricing !== null ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditingPricing(null)}
                  disabled={savingPricing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSavePricing(showPricingDialog.id, editingPricing)}
                  disabled={savingPricing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-pricing-btn"
                >
                  {savingPricing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Pricing
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowPricingDialog(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    // Initialize editing with current pricing
                    const currentPricing = showPricingDialog?.preferred_classes || [];
                    setEditingPricing([...currentPricing]);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="edit-pricing-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Pricing
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved Locations Dialog */}
      <Dialog open={!!showLocationsDialog} onOpenChange={() => { setShowLocationsDialog(null); setEditingLocations(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              Saved Locations
            </DialogTitle>
            <DialogDescription>
              {showLocationsDialog?.company ? `${showLocationsDialog.company} - ` : ''}{showLocationsDialog?.first_name} {showLocationsDialog?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Edit Mode */}
            {editingLocations !== null ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 mb-2">Add locations with Google-verified addresses for quick selection when booking classes.</p>
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {editingLocations.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-2">No saved locations</p>
                  ) : (
                    <div className="space-y-3 mb-3">
                      {editingLocations.map((location, idx) => {
                        const formattedAddress = location.address?.street 
                          ? `${location.address.street}, ${location.address.city || ''}, ${location.address.state || ''} ${location.address.zip || ''}`.replace(/,\s*,/g, ',').replace(/,\s*$/, '')
                          : '';
                        
                        return (
                          <div key={location.id || idx} className="p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 space-y-2">
                                <input
                                  type="text"
                                  placeholder="Location Name (e.g., Main Office)"
                                  value={location.name || ''}
                                  onChange={(e) => {
                                    const newLocations = [...editingLocations];
                                    newLocations[idx] = { ...newLocations[idx], name: e.target.value };
                                    setEditingLocations(newLocations);
                                  }}
                                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                                />
                                
                                <AddressAutocomplete
                                  initialValue={formattedAddress}
                                  placeholder="Search for address..."
                                  className="text-sm"
                                  onAddressSelect={(addressComponents, formatted, placeName) => {
                                    if (!addressComponents) return;
                                    
                                    const newLocations = [...editingLocations];
                                    newLocations[idx] = {
                                      ...newLocations[idx],
                                      address: {
                                        street: addressComponents.street || '',
                                        city: addressComponents.city || '',
                                        state: addressComponents.state || '',
                                        zip: addressComponents.zip || ''
                                      }
                                    };
                                    if (!newLocations[idx].name && placeName) {
                                      newLocations[idx].name = placeName;
                                    }
                                    setEditingLocations(newLocations);
                                    toast.success('Address verified');
                                  }}
                                />
                                
                                {location.address?.street && (
                                  <div className="p-2 bg-green-50 rounded border border-green-200">
                                    <div className="flex items-center gap-1 text-xs text-green-600 mb-1">
                                      <CheckCircle className="w-3 h-3" />
                                      <span className="font-medium">Verified Address</span>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {location.address.street}
                                      {location.address.city && `, ${location.address.city}`}
                                      {location.address.state && `, ${location.address.state}`}
                                      {location.address.zip && ` ${location.address.zip}`}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingLocations(editingLocations.filter((_, i) => i !== idx));
                                }}
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingLocations([
                        ...editingLocations,
                        { id: `temp-${Date.now()}`, name: '', address: { street: '', city: '', state: '', zip: '' } }
                      ]);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg border border-dashed border-orange-300 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Location
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              showLocationsDialog?.locations && showLocationsDialog.locations.length > 0 ? (
                <div className="space-y-3">
                  {showLocationsDialog.locations.map((location, idx) => (
                    <div key={idx} className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-600" />
                        {location.name || `Location ${idx + 1}`}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        {typeof location.address === 'object' 
                          ? `${location.address.street || ''}${location.address.city ? `, ${location.address.city}` : ''}${location.address.state ? `, ${location.address.state}` : ''} ${location.address.zip || ''}`.trim()
                          : location.address || 'No address specified'
                        }
                      </p>
                      {location.notes && (
                        <p className="text-xs text-gray-400 mt-2 italic">{location.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No saved locations</p>
                  <p className="text-xs text-gray-400 mt-1">Click the edit button below to add addresses</p>
                </div>
              )
            )}
          </div>
          
          <DialogFooter>
            {editingLocations !== null ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditingLocations(null)}
                  disabled={savingLocations}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSaveLocations(showLocationsDialog.id, editingLocations)}
                  disabled={savingLocations}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="save-locations-btn"
                >
                  {savingLocations ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Locations
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowLocationsDialog(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    // Initialize editing with current locations
                    const currentLocations = showLocationsDialog?.locations || [];
                    setEditingLocations([...currentLocations.map(loc => ({ ...loc }))]);
                  }}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="edit-locations-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Locations
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringClientsManager;
